import styled, { css, keyframes } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';

export const HighlightDiagonal = styled.span<{
  $clickable?: boolean;
}>`
  position: relative;
  display: inline-block;
  padding: 3px 3px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.primary};
    transform: skewX(-12deg);
    border-radius: 4px;
    z-index: 0;
  }

  span {
    position: relative;
    z-index: 1;
    color: #000; /* texto preto */
    font-weight: inherit;
  }

  ${({ $clickable }) =>
    $clickable &&
    css`
      transition: 200ms;
      cursor: pointer;
      &:hover {
        filter: brightness(1.1);
      }
    `}
`;

export const AdminActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING._8};

  @media (max-width: 600px) {
    width: 100%;
    flex-direction: column;

    button {
      width: 100% !important;
    }
  }
`;

export const HeroTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${SPACING._16};
  max-width: 1120px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: visible;
  padding-top: 300px;
  margin-bottom: ${SPACING._64};

  @media (max-width: 780px) {
    max-width: 80%;
  }

  h4 {
    position: relative;
    z-index: 100;
    max-width: 560px;
    margin: 0 auto;
  }

  @media (max-width: 600px) {
    padding-top: 200px;
  }
`;

export const HeroImageArc = styled.div`
  --arc-radius: 460px;
  --arc-card-size: 92px;
  position: absolute;
  left: 50%;
  top: 52px;
  transform: translateX(-50%);
  width: min(100%, 1400px);
  height: 260px;
  pointer-events: none;
  z-index: 20;
  --arc-card-size: 124px;

  @media (max-width: 1000px) {
    --arc-radius: 400px;
  }

  @media (max-width: 880px) {
    --arc-radius: 400px;
  }

  @media (max-width: 780px) {
    --arc-radius: 300px;
  }
`;

export const ArcTrack = styled.div`
  position: absolute;
  left: 50%;
  bottom: -230px;
  width: 1px;
  height: 1px;

  @media (max-width: 1000px) {
    bottom: -180px;
  }

  @media (max-width: 880px) {
    bottom: -140px;
  }

  @media (max-width: 780px) {
    bottom: -40px;
  }
`;

type ArcImageCardProps = {
  $angle: number;
  $tilt: number;
  $delay: number;
  $shuffleX: number;
  $shuffleY: number;
  $shuffleRotate: number;
  $enableShuffleEntry?: boolean;
};

const buildArcTransform = (angle: number, tilt: number): string =>
  `translate(-50%, -50%) rotate(${angle}deg) translateY(calc(var(--arc-radius) * -1)) rotate(${tilt}deg)`;

const shuffleEntryAnimation = (
  angle: number,
  tilt: number,
  shuffleX: number,
  shuffleY: number,
  shuffleRotate: number
): ReturnType<typeof keyframes> => keyframes`
  from {
    opacity: 0;
    filter: blur(6px);
    transform: ${buildArcTransform(
      angle,
      tilt
    )} translate(${shuffleX}px, ${shuffleY}px) rotate(${shuffleRotate}deg) scale(0.86);
  }

  65% {
    opacity: 1;
    filter: blur(0);
    transform: ${buildArcTransform(angle, tilt)} rotate(${
  shuffleRotate * 0.2
}deg) scale(1.04);
  }

  to {
    opacity: 1;
    filter: blur(0);
    transform: ${buildArcTransform(angle, tilt)};
  }
`;

export const ArcImageCard = styled.div<ArcImageCardProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: var(--arc-card-size);
  height: var(--arc-card-size);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.neutral700};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  transform: ${({ $angle, $tilt }) => buildArcTransform($angle, $tilt)};
  transform-origin: center;
  transition: transform 200ms ease;
  pointer-events: auto;
  opacity: 1;
  ${({
    $enableShuffleEntry,
    $angle,
    $tilt,
    $shuffleX,
    $shuffleY,
    $shuffleRotate,
  }) =>
    $enableShuffleEntry
      ? css`
          animation: ${shuffleEntryAnimation(
              $angle,
              $tilt,
              $shuffleX,
              $shuffleY,
              $shuffleRotate
            )}
            780ms cubic-bezier(0.2, 0.7, 0.2, 1);
        `
      : css`
          animation: fade-in-arc 420ms ease-out;
        `}
  animation-delay: ${({ $delay }) => `${$delay}ms`};
  animation-fill-mode: backwards;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    -webkit-user-drag: none;
  }

  &:hover {
    transform: ${({ $angle, $tilt }) =>
      `${buildArcTransform($angle, $tilt)} scale(1.06)`};
  }

  @keyframes fade-in-arc {
    from {
      opacity: 0;
      filter: blur(2px);
    }
    to {
      opacity: 1;
      filter: blur(0);
    }
  }
`;

export const SubscribeCta = styled.button`
  margin: 0 auto;
  z-index: 110;
  border: 1px solid ${({ theme }) => theme.primary};
  outline: none;
  cursor: pointer;
  padding: 10px 32px;
  border-radius: 9999px;
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.neutral1000};
  background: ${({ theme }) => theme.primary};
  transition: filter 200ms ease, transform 200ms ease;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.1);
    transform: scale(1.03);
  }

  &:active {
    transform: scale(0.97);
  }

  @media (max-width: 600px) {
    padding: 8px 28px;
    font-size: 13px;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${SPACING._24};
  width: 100%;
  position: relative;
  animation: fade-in-up 400ms ease-out;

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 600px) {
    gap: ${SPACING._24};
  }

  h1,
  h2,
  h6 {
    z-index: 1;
  }

  h1 {
    font-size: 4.5rem;
    line-height: 1;
    letter-spacing: -0.025em;
  }

  h6 {
    font-size: 1.5rem;
    line-height: 2rem;
  }
`;
